import type {IClientModel} from "../../Model/Client/ClientModel.js";
import {KendoToolBarHandler} from "../../Tools/Misc.js"
import {ClientRead} from "../../Controller/Client/ClientRead.js";
import {ClientUpsert} from "../../Controller/Client/ClientUpsert.js";
import {ClientDelete} from "../../Controller/Client/ClientDelete";
import {NotificationHelper} from "../../Helper/NotificationHelper";
import {ServiceAccountPanel} from "./ServiceAccountPanel.js";

export class ClientEditor {
    #clientId: string | undefined;
    #isValid: boolean = false;
    #jqContainer: JQuery | undefined;
    #jqFormContainer: JQuery | undefined;
    #jqServiceAccountContainer: JQuery | undefined;
    readonly #jqMainContainer: JQuery | undefined;
    #jqTool: JQuery | undefined;
    #kToolBar: kendo.ui.ToolBar | undefined;
    #kForm: kendo.ui.Form | undefined;
    #model: IClientModel | undefined;
    #serviceAccountPanel: ServiceAccountPanel | undefined;

    constructor(options: IClientEditorOptions) {
        this.#jqMainContainer = options.jqMainContainer;
        this.#clientId = options.clientId;
        this.#model = options.model;
    }

    public Init = async () => {
        this.#jqMainContainer.empty();
        await this.#buildHtml();
        this.#buildTool().then();
        this.#buildForm().then();
        this.#serviceAccountPanel = new ServiceAccountPanel({
            jqMainContainer: this.#jqServiceAccountContainer,
            clientId: this.#clientId,
        });
        this.#serviceAccountPanel.Init().then();
    }

    #buildForm = async () => {
        this.#kForm = this.#jqFormContainer.kendoForm({
            formData: this.#model,
            orientation: "horizontal",
            size: "small",
            items: [
                {field: "RaisonSociale", label: "RaisonSociale", validation: {required: true}},
                {
                    field: "Primarykey", label: "Id",
                    attributes: {"circ-id": "clientId"},
                    editorOptions: {
                        enable: true,
                        suffixOptions: {icon: "copy"}
                    }, validation: {required: false}
                },
            ],
            change: (e: kendo.ui.FormChangeEvent) => {
                this.#model[e.field] = e.value;
            },
            validate: (e: kendo.ui.FormValidateEvent) => {
                this.#isValid = e.valid;
            }
        }).data("kendoForm");

        this.#setFormSize().then();
        
        const input = this.#kForm.element.find("[circ-id='clientId']");
        input.prop("disabled", true);
        input.siblings(".k-input-suffix").on("click", (e) => {
            navigator.clipboard.writeText(input.val().toString())
                .then(() => NotificationHelper.Instance.success("L'Id a été copié dans le presse-papier"));
        });
    }

    #buildHtml = async () => {
        this.#jqContainer = $("<div></div>")
            .css({
                height: "100%",
                width: "100%",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column"
            }).appendTo(this.#jqMainContainer);

        this.#jqFormContainer = $("<div></div>")
            .css({
                display: "flex",
                flexDirection: "column",
                width: "50%",
            }).appendTo(this.#jqContainer);

        // Le bloc « Compte de service » suit directement le formulaire et prend
        // l'espace restant : la barre d'outils reste en bas.
        this.#jqServiceAccountContainer = $("<div></div>")
            .css({padding: "8px 0", flexGrow: "1"})
            .appendTo(this.#jqContainer);

        const div = $("<div></div>")
            .css({
                width: "100%",
            }).appendTo(this.#jqContainer);

        this.#jqTool = $("<div></div>")
            .appendTo(div);
    }

    #buildTool = async () => {
        this.#kToolBar = this.#jqTool.kendoToolBar({
            size: "small",
            items: [
                {
                    type: "button",
                    text: "Enregistrer",
                    icon: "save",
                    click: async (e: any) => KendoToolBarHandler.AsyncToolbarHandle(e.target, this.#saveClient),
                    themeColor: "success"
                },
                {
                    type: "button",
                    text: "Supprimer",
                    icon: "trash",
                    themeColor: "error",
                    click: async (e: any) => this.#deleteClient(),
                }
            ]
        }).data("kendoToolBar");
    }


    #saveClient = async () => {
        this.#kForm.validate();
        if (!this.#isValid)
            return;

        return ClientUpsert.Upsert(this.#model);
    }

    #deleteClient = async () => {
        kendo.confirm("Bien certain de vouloir supprimer ce client ? </br> Vous allez supprimer tous les projets attenants.")
            .then(() => {
                ClientDelete.Delete(this.#model).then();
            });
    }

    #setFormSize = async () => {
        this.#kForm.element.find(".k-form-buttons").remove();
    }

    public Dispose = async () => {
        this.#serviceAccountPanel?.Dispose();
        this.#kForm.destroy();
        this.#kToolBar.destroy();
        this.#jqMainContainer.empty();
    }
}

export interface IClientEditorOptions {
    clientId: string;
    jqMainContainer: JQuery;
    model: IClientModel
}